import { ConfigurationFormFileComponent } from './configuration-form-file/configuration-form-file.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorType } from '../../../../core/routing/error-types';
import { ToastService } from '../../../../base/services/toast.service';
import { ModalService } from '../../../../base/shared/modal/modal.service';
import { ImportConfigurationDTO, ImportConfigurationTransferType, ImportConfigurationType, ImportType } from '../../../../base/models/models.swagger.generated';
import { ImportConfigurationEditFormsCoordinatorService } from './import-configuration-edit-forms-coordinator.service';
import { Component, OnInit, Output, ViewChild, EventEmitter, Input, OnDestroy, AfterViewChecked, ChangeDetectorRef, input } from '@angular/core';
import { SelectingEventArgs, TabComponent, TabModule } from '@syncfusion/ej2-angular-navigations';
import * as pages from '../../../../core/routing/page-names';
import { Location } from '@angular/common';
import { map, merge, Observable, of, raceWith, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationFormAs2Component } from './configuration-form-as2/configuration-form-as2.component';
import { ConfigurationFormRestComponent } from './configuration-form-rest/configuration-form-rest.component';
import { ConfigurationFormFtpComponent } from './configuration-form-ftp/configuration-form-ftp.component';
import { ConfigurationFormSftpComponent } from './configuration-form-sftp/configuration-form-sftp.component';
import { ConfigurationFormEmailComponent } from './configuration-form-email/configuration-form-email.component';
import { ConfigurationFormTimesceduleComponent } from './configuration-form-timescedule/configuration-form-timescedule.component';
import { ConfigurationFormGeneralComponent } from './configuration-form-general/configuration-form-general.component';
import { ConfigurationFormResponseComponent } from './configuration-form-response/configuration-form-response.component';
import { UrlHelper } from 'src/app/base/shared/url-helper';
import { ConfigurationFormAcodisComponent } from './configuration-form-acodis/configuration-form-acodis.component';
import { ConfigurationFormManualImportComponent } from "./configuration-form-manual-import/configuration-form-manual-import.component";
import { MessageDirective, MessageSeverity, MessageVariant } from 'src/app/directives/message.directive';

export interface IImportConfigurationEditSettings {
  importConfigurationRoute: string;
  importConfigurationPageName: string;
  supportResponseConfig: boolean;
}

@Component({
    selector: 'app-import-configuration-edit-forms',
    templateUrl: './import-configuration-edit-forms.component.html',
    styleUrls: ['./import-configuration-edit-forms.component.scss'],
    imports: [TabModule, ConfigurationFormGeneralComponent, ConfigurationFormTimesceduleComponent, ConfigurationFormEmailComponent, ConfigurationFormSftpComponent, ConfigurationFormFtpComponent, ConfigurationFormRestComponent, ConfigurationFormAs2Component, ConfigurationFormFileComponent, TranslateModule, ConfigurationFormResponseComponent, ConfigurationFormAcodisComponent, ConfigurationFormManualImportComponent, MessageDirective]
})
export class ImportConfigurationEditFormsComponent implements OnInit, OnDestroy, AfterViewChecked {
  public settings = input.required<IImportConfigurationEditSettings>();

  @ViewChild('tabObj')
  public tabObj: TabComponent;

  @ViewChild('configurationFormFileComponent')
  public configurationFormFileComponent: ConfigurationFormFileComponent;

  @Input()
  public canEdit: boolean = false;

  @Input()
  public set configuration(value: ImportConfigurationDTO) {
    if (value !== this.formsCoordinationService.configuration) {
      this.formsCoordinationService.configuration = value;
    }
  }

  @Output()
  public save = new EventEmitter<ImportConfigurationDTO>();
  @Output()
  public delete = new EventEmitter<number>();
  @Output()
  public cancel = new EventEmitter();

  public get transferType(): ImportConfigurationTransferType {
    return this.formsCoordinationService.configurationFormGeneral.controls.transferType.value;
  }

  public get importType(): ImportType {
    return this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
  }

  public get isTemplate(): boolean {
    return this.formsCoordinationService.configuration?.type === ImportConfigurationType.Template;
  }

  public animation: object = {
    previous: { effect: "", duration: 0, easing: "" },
    next: { effect: "", duration: 0, easing: "" }
  };

  public ImportConfigurationTransferType = ImportConfigurationTransferType;

  private isGetUrl = false;

  MessageVariant = MessageVariant;
  MessageSeverity = MessageSeverity;

  private subscriptions = new Subscription();

  constructor(
    public formsCoordinationService: ImportConfigurationEditFormsCoordinatorService,
    private modalService: ModalService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit() {
    this.subscriptions.add(
      this.formsCoordinationService.configurationFormGeneral.controls.transferType.valueChanges.subscribe(() => {
        this.setTabVisibility();
      })
    );
    this.subscriptions.add(
      this.formsCoordinationService.configurationFormGeneral.controls.importType.valueChanges.subscribe(() => {
        this.setTabVisibility();
      })
    );
    this.subscriptions.add(
      this.formsCoordinationService.configurationFormFile.controls.sendResponse.valueChanges.subscribe(() => {
        this.setTabVisibility();
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.formsCoordinationService.configuration = null;
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public isFormDirty(): boolean {
    return this.formsCoordinationService.isFormDirty();
  }

  public duplicateActualConfiguration(): ImportConfigurationDTO {
    return this.formsCoordinationService.duplicateActualConfiguration();
  }

  public deleteConfiguration(): void {
    if (this.formsCoordinationService.configuration?.id > 0) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.import-configuration.messages.delete-confirm-title', 'i18n.domain.import-configuration.messages.delete-confirm')
      dialog.onOkSubject.subscribe(() => {
        const id = this.formsCoordinationService.configuration.id;
        this.formsCoordinationService.configuration = null;
        this.delete.emit(id);
      });
    } else {
      this.forceClose();
    }
  }

  public canCloseForm(): Observable<boolean> {
    if (this.formsCoordinationService.isFormDirty()) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.import-configuration.edit.discard-dialog.title', 'i18n.domain.import-configuration.edit.discard-dialog.question');
      const okObservable = dialog.onOkSubject.pipe(map(() => {
        return true;
      }));
      const cancelObservable = dialog.onCancelSubject.pipe(map(() => false));
      return okObservable.pipe(raceWith(cancelObservable));
    } else {
      return of(true);
    }
  }

  public closeForm(): void {
    this.canCloseForm().subscribe((canClose: boolean) => {
      if (canClose) {
        this.forceClose();
      }
    });
  }

  public forceClose(): void {
    this.formsCoordinationService.configuration = null;
    this.cancel.emit();
  }

  public onSave(): void {
    this.formsCoordinationService.validateWholeForm();
    if (this.formsCoordinationService.isWholeFormValid) {
      const configuration = this.formsCoordinationService.getDtoFromForm();
      this.save.emit(configuration);
    } else {
      this.formsCoordinationService.setFormDirty();
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.import-configuration.form-invalid.title',
          'i18n.domain.import-configuration.form-invalid.message', null);
      }
    }
  }

  public select(event: SelectingEventArgs): void {
    if (event.isSwiped) {
      event.cancel = true;
      return;
    }
    this.setUrlRoute(event.selectingIndex);
  }

  public tabGroupCreated() {
    this.setTabVisibility();
    this.getUrlRoute();
  }

  private getUrlRoute() {
    if (this.route.children.length === 0) {
      return;
    }

    var pageRoute = UrlHelper.getTabSegmentForConfigurations(this.router, this.settings().importConfigurationPageName);

    if (pageRoute == null || pageRoute == undefined) {
      return;
    }

    this.isGetUrl = true;

    switch (pageRoute) {
      case pages.configurationGeneralPageName:
        this.tabObj.select(this.TAB_GENERAL);
        break;
      case pages.configurationTimeSchedulePageName:
        this.isTabVisible(this.TAB_TIMESCHEDULE) ? this.tabObj.select(this.TAB_TIMESCHEDULE) : this.goToTabDefault();
        break;
      case pages.configurationEmailPageName:
        this.isTabVisible(this.TAB_EMAIL) ? this.tabObj.select(this.TAB_EMAIL) : this.goToTabDefault();
        break;
      case pages.configurationSftpPageName:
        this.isTabVisible(this.TAB_SFTP) ? this.tabObj.select(this.TAB_SFTP) : this.goToTabDefault();
        break;
      case pages.configurationFtpPageName:
        this.isTabVisible(this.TAB_FTP) ? this.tabObj.select(this.TAB_FTP) : this.goToTabDefault();
        break;
      case pages.configurationRestPageName:
        this.isTabVisible(this.TAB_REST) ? this.tabObj.select(this.TAB_REST) : this.goToTabDefault();
        break;
      case pages.configurationAs2PageName:
        this.isTabVisible(this.TAB_AS2) ? this.tabObj.select(this.TAB_AS2) : this.goToTabDefault();
        break;
      case pages.configurationFilePageName:
        this.isTabVisible(this.TAB_FILE) ? this.tabObj.select(this.TAB_FILE) : this.goToTabDefault();
        break;
      case pages.configurationAcodisPageName:
        this.isTabVisible(this.TAB_ACODIS) ? this.tabObj.select(this.TAB_ACODIS) : this.goToTabDefault();
        break;
      case pages.configurationResponsePageName:
        this.isTabVisible(this.TAB_RESPONSE) ? this.tabObj.select(this.TAB_RESPONSE) : this.goToTabDefault();
        break;
      case pages.configurationManualImportPageName:
        this.isTabVisible(this.TAB_MANUAL_IMPORT) ? this.tabObj.select(this.TAB_MANUAL_IMPORT) : this.goToTabDefault();
        break;
      default:
        this.goToTabDefault()
        break;
    }
  }

  private setUrlRoute(index: number) {
    //Catch the call of the function when comming over url
    if (this.isGetUrl) {
      this.isGetUrl = false;
      return;
    }

    var id = UrlHelper.getIdForSegment(this.settings().importConfigurationPageName, this.router);
    var base = this.settings().importConfigurationRoute + '/' + (id ?? pages.importConfigurationCreatePageName) + '/';
    switch (index) {
      case this.TAB_GENERAL:
        this.location.replaceState(base + pages.configurationGeneralPageName);
        break;
      case this.TAB_TIMESCHEDULE:
        this.location.replaceState(base + pages.configurationTimeSchedulePageName);
        break;
      case this.TAB_EMAIL:
        this.location.replaceState(base + pages.configurationEmailPageName);
        break;
      case this.TAB_SFTP:
        this.location.replaceState(base + pages.configurationSftpPageName);
        break;
      case this.TAB_FTP:
        this.location.replaceState(base + pages.configurationFtpPageName);
        break;
      case this.TAB_REST:
        this.location.replaceState(base + pages.configurationRestPageName);
        break;
      case this.TAB_AS2:
        this.location.replaceState(base + pages.configurationAs2PageName);
        break;
      case this.TAB_MANUAL_IMPORT:
        this.location.replaceState(base + pages.configurationManualImportPageName);
        break;
      case this.TAB_FILE:
        this.location.replaceState(base + pages.configurationFilePageName);
        this.configurationFormFileComponent?.getUrlRoute();
        break;
      case this.TAB_RESPONSE:
        this.location.replaceState(base + pages.configurationResponsePageName);
        break;
      case this.TAB_ACODIS:
        this.location.replaceState(base + pages.configurationAcodisPageName);
        break;
    }
  }

  private setTabVisibility() {
    if (!this.tabObj) {
      return;
    }
    this.tabObj.hideTab(this.TAB_TIMESCHEDULE, !this.isTabVisible(this.TAB_TIMESCHEDULE));
    this.tabObj.hideTab(this.TAB_EMAIL, !this.isTabVisible(this.TAB_EMAIL));
    this.tabObj.hideTab(this.TAB_SFTP, !this.isTabVisible(this.TAB_SFTP));
    this.tabObj.hideTab(this.TAB_FTP, !this.isTabVisible(this.TAB_FTP));
    this.tabObj.hideTab(this.TAB_REST, !this.isTabVisible(this.TAB_REST));
    this.tabObj.hideTab(this.TAB_AS2, !this.isTabVisible(this.TAB_AS2));
    this.tabObj.hideTab(this.TAB_MANUAL_IMPORT, !this.isTabVisible(this.TAB_MANUAL_IMPORT));
    this.tabObj.hideTab(this.TAB_FILE, !this.isTabVisible(this.TAB_FILE));
    this.tabObj.hideTab(this.TAB_RESPONSE, !this.isTabVisible(this.TAB_RESPONSE));
    this.tabObj.hideTab(this.TAB_ACODIS, !this.isTabVisible(this.TAB_ACODIS));
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.formsCoordinationService.isFormDirty()) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.configurations.discard-dialog.title', 'i18n.domain.export-configuration.configurations.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => true)),
      );
    }
    return true;
  }

  private isTabVisible(tabIndex: number): boolean {
    switch (tabIndex) {
      case this.TAB_TIMESCHEDULE:
        return this.transferType == ImportConfigurationTransferType.FTPClient;
      case this.TAB_EMAIL:
        return this.transferType == ImportConfigurationTransferType.Email;
      case this.TAB_SFTP:
        return this.transferType == ImportConfigurationTransferType.SFTPServer;
      case this.TAB_FTP:
        return this.transferType == ImportConfigurationTransferType.FTPClient;
      case this.TAB_REST:
        return this.transferType == ImportConfigurationTransferType.REST;
      case this.TAB_AS2:
        return this.transferType == ImportConfigurationTransferType.AS2;
      case this.TAB_MANUAL_IMPORT:
        return this.transferType == ImportConfigurationTransferType.ManualImport;
      case this.TAB_FILE:
        return this.transferType != ImportConfigurationTransferType.REST
          && this.transferType != ImportConfigurationTransferType.Acodis
          && this.transferType != ImportConfigurationTransferType.Magento
          && this.transferType != ImportConfigurationTransferType.Zir;
      case this.TAB_RESPONSE:
        return this.formsCoordinationService.configurationFormFile.controls.sendResponse.value;
      case this.TAB_ACODIS:
        return this.transferType == ImportConfigurationTransferType.Acodis;
      default:
        return true;
    }
  }

  private goToTabDefault() {
    var id = UrlHelper.getIdForSegment(this.settings().importConfigurationPageName, this.router);
    this.router.navigate([this.settings().importConfigurationRoute + '/' + (id ? id + '/' + pages.configurationGeneralPageName : '')]);
  }

  private TAB_GENERAL = 0;
  private TAB_TIMESCHEDULE = 1;
  private TAB_EMAIL = 2;
  private TAB_SFTP = 3;
  private TAB_FTP = 4;
  private TAB_REST = 5;
  private TAB_AS2 = 6;
  private TAB_MANUAL_IMPORT = 7;
  private TAB_FILE = 8;
  private TAB_RESPONSE = 9;
  private TAB_ACODIS = 10;
}
